import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { BUSINESS_LEXICON } from "@/lib/renata/experience/lexicon";

export const Route = createFileRoute("/app/renata/collaboration")({
  component: CollaborationPage,
  head: () => ({ meta: [{ title: "Trabajo en equipo — Renata" }] }),
});

const KIND_ES: Record<string, string> = {
  collaboratesWith: "trabaja con",
  consults: "consulta a",
  dependsOn: "se apoya en",
  fallbackTo: "alternativa",
};

function CollaborationPage() {
  const tenantId = useWorkspaceTenantId();
  const [edges, setEdges] = useState<Array<Record<string, unknown>>>([]);

  useEffect(() => {
    if (!tenantId) return;
    void fetch("/api/renata/planning?view=collaboration", {
      credentials: "include",
      headers: clientRequestHeaders(tenantId),
    })
      .then((r) => r.json())
      .then((d) => {
        setEdges(d.edges ?? []);
      });
  }, [tenantId]);

  const pairs = useMemo(() => {
    return edges.map((e) => {
      const kind = String(e.kind ?? "");
      return {
        from: String(e.from ?? ""),
        to: String(e.to ?? ""),
        relation: KIND_ES[kind] ?? BUSINESS_LEXICON.collaboratesWith,
        reason: String(e.reason ?? ""),
      };
    });
  }, [edges]);

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
      <RenataPageChrome
        screenId="collaboration"
        title="Trabajo en equipo"
        subtitle="Renata combina especialistas cuando una tarea necesita más de un tipo de conocimiento — por ejemplo ventas + inventario + marketing."
        showBack
        breadcrumb={["Equipo", "Trabajo en equipo"]}
      />

      {pairs.length === 0 ? (
        <div className="rounded-xl border border-dashed p-6 text-center space-y-2">
          <p className="text-sm font-medium">Todavía no hay colaboraciones registradas</p>
          <p className="text-xs text-muted-foreground">
            Cuando Renata combine especialistas para un caso real, vas a ver acá quién aportó qué.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold">Cómo se combinan</h2>
          {pairs.map((p, i) => (
            <div key={i} className="rounded-lg border px-3 py-2 text-sm">
              <p>
                <span className="font-medium">{p.from}</span>
                <span className="text-muted-foreground"> {p.relation} </span>
                <span className="font-medium">{p.to}</span>
              </p>
              {p.reason ? <p className="text-xs text-muted-foreground mt-0.5">{p.reason}</p> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
